/**
 * <s-free-shipping> keeps the free-shipping progress bar in step with the cart.
 * The first state is rendered by Liquid; this refreshes it from the cart JSON
 * after a product is added and whenever a dialog (the cart drawer) closes.
 */
class FreeShipping extends HTMLElement {
  #controller = null;
  #timers = [];

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (form instanceof HTMLFormElement && form.querySelector('[name="id"]')) this.#refreshSoon();
    }, { signal, capture: true });

    document.addEventListener('close', () => this.#refreshSoon(), { signal, capture: true });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.#refresh();
    }, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#timers.forEach(clearTimeout);
    this.#timers = [];
  }

  #refreshSoon() {
    this.#timers.forEach(clearTimeout);
    this.#timers = [900, 2500].map((delay) => setTimeout(() => this.#refresh(), delay));
  }

  async #refresh() {
    const url = this.dataset.cartUrl;
    if (!url) return;

    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.#controller?.signal });
      if (!response.ok) return;
      const cart = await response.json();
      this.#render(Number(cart.total_price) || 0, cart.currency);
    } catch {
      // Keep the current state if the cart can't be read.
    }
  }

  #render(totalCents, currency) {
    const threshold = Number(this.dataset.threshold) || 0;
    if (threshold <= 0) return;

    const remaining = Math.max(threshold - totalCents, 0);
    const percent = Math.min(Math.round((totalCents / threshold) * 100), 100);

    const message = this.querySelector('[data-free-shipping-message]');
    if (message) {
      if (remaining > 0) {
        const amount = new Intl.NumberFormat(currency === 'AUD' ? 'en-AU' : (document.documentElement.lang || undefined), {
          style: 'currency',
          currency: currency || 'AUD',
        }).format(remaining / 100);
        message.innerHTML = (this.dataset.remainingTemplate || '').replace('__AMOUNT__', amount);
      } else {
        message.innerHTML = this.dataset.unlocked || '';
      }
    }

    this.querySelector('[data-free-shipping-bar]')?.setAttribute('aria-valuenow', String(percent));
    const fill = this.querySelector('[data-free-shipping-fill]');
    if (fill instanceof HTMLElement) fill.style.width = `${percent}%`;
  }
}

if (!customElements.get('s-free-shipping')) customElements.define('s-free-shipping', FreeShipping);
